import React, { useState } from "react";
import chat from "../assets/chat.svg";
import { FaRegCalendar } from "react-icons/fa";
import { format, parseISO } from "date-fns";
import alertTriangle from "../assets/alert-triangle.svg";
import carIcon from "../assets/carIcon.svg";
import { VscFeedback } from "react-icons/vsc";
import DownloadIcon from "../components/icons/DownloadIcon";
import Spinner from "./Spinner";

function isImageUrl(url) {
  return /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(url);
}

const estadoMapping = {
  1: "Agendado",
  2: "Pendiente",
  3: "En espera",
  4: "Confirmado",
  5: "Cerrado",
  6: "Rechazado",
  7: "Cancelado",
};

// Nueva función para convertir la hora a formato AM/PM
function formatHourToAmPm(hourString) {
  const [hours, minutes] = hourString.split(":").map(Number);
  return `${hours}:${String(minutes).padStart(2, "0")} `;
}

function DashboardTable({
  columnsConfig,
  data,
  loading,
  showCheckboxes = false,
  onActionClick,
  onCloseTripClick,
  onSendInfo,
  onSendRating,
  onDownloadDetail,
  currentSearch,
  onSearchChange,
  onSearchSubmit,
  onChangeStatus,
}) {
  const handleSearchChange = (event) => {
    onSearchChange(event); // Mantiene la actualización del estado de búsqueda
  };
  return (
    <div className="inter-regular">
      {loading ? (
        <Spinner />
      ) : (
        <>
          <div className="flex items-center gap-2 mb-4">
            <input
              id="search-input"
              type="text"
              placeholder="Buscar..."
              value={currentSearch}
              onChange={handleSearchChange}
              className="p-2 border border-gray-300 rounded-l-md w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <button
              onClick={() => onSearchSubmit?.()}
              className="px-4 py-2 bg-[#59A8D4] text-white rounded-md"
            >
              Buscar
            </button>
          </div>

          <table className="min-w-full bg-white">
            <thead>
              <tr>
                {showCheckboxes && (
                  <th className="text-xs pl-4 py-2 border-b text-left text-[#D9D9D9]">
                    <input type="checkbox" className="accent-[#0080FF]" />
                  </th>
                )}
                {columnsConfig.map((column, index) => (
                  <th
                    key={index}
                    className={`font-bold text-xs py-2 border-b text-left text-[#1F384C] border-r ${
                      index === 0 ? "px-6" : "px-4"
                    }`}
                  >
                    {column.headerName}
                  </th>
                ))}
                <th className="text-xs px-4 py-2 border-b text-left text-[#1F384C]">
                  Acción
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.map((row, rowIndex) => (
                <DashboardTableRow
                  key={`${row.id}-${row.status}`} // 🔁 Clave cambia al cambiar estados
                  row={row}
                  columnsConfig={columnsConfig}
                  showCheckboxes={showCheckboxes}
                  onActionClick={onActionClick}
                  onCloseTripClick={onCloseTripClick}
                  onSendInfo={onSendInfo}
                  onSendRating={onSendRating}
                  onDownloadDetail={onDownloadDetail}
                  onChangeStatus={onChangeStatus}
                />
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function DashboardTableRow({
  row,
  columnsConfig,
  showCheckboxes,
  onActionClick,
  onCloseTripClick,
  onSendInfo,
  onSendRating,
  onDownloadDetail,
  onChangeStatus,
}) {
  const [showPopup, setShowPopup] = useState(false);
  const [showEstadoSelector, setShowEstadoSelector] = useState(false);
  const handleActionClick = () => {
    setShowPopup((prev) => !prev);
  };

  const handleDetailsClick = () => {
    setShowPopup(false);
    if (onActionClick) {
      onActionClick(row);
    }
  };

  const handleSendInfo = () => {
    setShowPopup(false);
    if (onSendInfo) {
      onSendInfo(row);
    }
  };

  const handleSendRating = () => {
    setShowPopup(false);
    if (onSendRating) {
      onSendRating(row);
    }
  };

  const handleDownloadDetail = () => {
    setShowPopup(false);
    if (onDownloadDetail) {
      onDownloadDetail(row);
    }
  };

  const handleCloseTripClick = () => {
    setShowPopup(false);
    if (onCloseTripClick) {
      onCloseTripClick(row);
    }
  };

  const estadoColors = {
    1: "#A5D3FF",
    2: "#FFA500",
    3: "#B0B0B0",
    4: "#4AA785",
    5: "#D9D9D9",
    6: "#E66672",
    7: "#BE202F",
  };

  const renderEstado = (estado) => (
    <div className="relative">
      <div
        className="flex items-center space-x-2 cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          setShowEstadoSelector(!showEstadoSelector);
        }}
      >
        <span
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: estadoColors[estado] || "#D9D9D9" }}
        ></span>
        <span style={{ color: estadoColors[estado] || "#D9D9D9" }}>
          {estadoMapping[estado] || "N/A"}
        </span>
      </div>

      {showEstadoSelector && (
        <div className="absolute z-20 mt-1 w-32 bg-white shadow-lg rounded border border-gray-200">
          {Object.entries(estadoMapping).map(([key, value]) => (
            <div
              key={key}
              onClick={async () => {
                if (row.status !== Number(key)) {
                  setShowEstadoSelector(false); // Cerramos inmediatamente
                  await onChangeStatus(row, Number(key)); // Cambiamos el estado
                }
              }}
              className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer"
              style={{ color: estadoColors[key] }}
            >
              {value}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <tr
      style={{
        backgroundColor: row.status === 4 ? "#F7F9FB" : "none",
      }}
    >
      {showCheckboxes && (
        <td className="text-sm pl-4 py-2 border-b-[0.5px] text-left text-[#D9D9D9]">
          <input type="checkbox" className="accent-[#0080FF]" />
        </td>
      )}
      {columnsConfig.map((column, index) => (
        <td
          key={index}
          className={`relative px-4 py-2 border-b-[0.5px] text-xs cursor-pointer`}
          onClick={column.field === "status" ? undefined : handleDetailsClick}
        >
          {column.field === "status" ? (
            renderEstado(row.status)
          ) : column.field === "client.name" ? (
            <div className="flex items-center gap-x-3">
              <img
                src={row.client.logo}
                alt="Logo del cliente"
                className="w-6 h-6 rounded-full"
              />
              <p>{column.field.split(".").reduce((o, i) => o?.[i], row)}</p>
            </div>
          ) : column.field === "client.isCorporate" ? (
            row[column.field] ? (
              "Corporativo"
            ) : (
              "Particular"
            )
          ) : typeof column.field === "string" && column.field.includes(".") ? (
            column.field.split(".").reduce((o, i) => o?.[i], row) || (
              <div className="flex items-center space-x-2">
                <div
                  style={{
                    display: "flex",
                    gap: 5,
                    position: "absolute",
                    left: "3%",
                  }}
                >
                  <img src={alertTriangle} alt="Logo sin conductor" />
                  <p>No asignado</p>
                </div>
              </div>
            )
          ) : column.field === "date" ? (
            <div className="flex items-center gap-x-1">
              <FaRegCalendar color="rgba(28, 28, 28, 0.4)" />
              <p>{format(parseISO(row["date"]), "dd/MM/yyyy")}</p>
            </div>
          ) : column.field === "hour" ? (
            <p className="w-8">{formatHourToAmPm(row["hour"])}</p>
          ) : column.field === "pickup" || column.field === "destination" ? (
            (() => {
              const fullText = row[column.field]; // Obtén el texto completo según el campo
              const truncatedText =
                fullText && fullText.length > 20
                  ? `${fullText.slice(0, 20)}...`
                  : fullText;

              return fullText ? (
                <div
                  className="relative group"
                  style={{
                    maxWidth: "200px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={fullText} // Tooltip nativo del navegador
                >
                  {truncatedText}
                  {/* Tooltip personalizado visible al hover */}
                  <div
                    className="absolute left-0 top-full mt-1 hidden group-hover:block bg-gray-800 text-white text-sm p-2 rounded shadow-lg"
                    style={{
                      whiteSpace: "normal",
                      maxWidth: "300px",
                      zIndex: 10,
                    }}
                  >
                    {fullText}
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <div
                    style={{
                      display: "flex",
                      gap: 5,
                      position: "absolute",
                      left: "3%",
                      top: "40%",
                    }}
                  >
                    <img src={alertTriangle} alt="Logo sin conductor" />
                    <p>No asignado</p>
                  </div>
                </div>
              );
            })()
          ) : (
            <p className="w-10">{row[column.field] || "N/A"}</p>
          )}
        </td>
      ))}
      <td className="w-10 px-2 py-4 border-b text-xs relative">
        <button
          onClick={handleActionClick}
          className="focus:outline-none flex space-x-1 items-center"
        >
          <span className="w-1 h-1 bg-[#D9D9D9] rounded-full"></span>
          <span className="w-1 h-1 bg-[#D9D9D9] rounded-full"></span>
          <span className="w-1 h-1 bg-[#D9D9D9] rounded-full"></span>
        </button>
        {showPopup && (
          <div
            style={{
              position: "absolute",
              left: "68%",
              transform: "translateX(-90%)",
              top: "100%",
              width: "230px",
              padding: "8px",
              borderRadius: "4px",
              backgroundColor: "white",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
              zIndex: 10,
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "-6px",
                right: "33%",
                width: 0,
                height: 0,
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderBottom: "6px solid white",
              }}
            ></div>
            <button
              onClick={handleSendInfo}
              className="flex py-3 px-2 text-[#2C2C2C] text-[10px] hover:bg-[#F8F8F8] w-full"
            >
              <img src={chat} alt="Logo de información" className="mr-3" />
              Enviar información del viaje
            </button>
            <button
              onClick={handleDownloadDetail}
              className="flex py-3 px-2 text-[#2C2C2C] text-[10px] hover:bg-[#F8F8F8] w-full"
            >
              <div className="mr-3">
                <DownloadIcon size={18} color="black" />
              </div>
              Descargar detalle del viaje
            </button>
            <button
              onClick={handleSendRating}
              className="flex py-3 px-2 text-[#4AA785] text-[10px] hover:bg-[#F8F8F8] w-full"
            >
              <div className="mr-3">
                <VscFeedback size={20} color="#4AA785" />
              </div>
              Enviar evaluación del servicio
            </button>
            <button
              onClick={handleCloseTripClick}
              className="flex py-3 px-2 mt-2 text-[#BE202F] text-[10px] hover:bg-[#F8F8F8] w-full"
            >
              <img src={carIcon} alt="Logo de cerrar viaje" className="mr-3" />
              Cerrar viaje
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default DashboardTable;
